<template>

	<div class="main-section">

		<section class="content-section">

			<div class="text-block full-width">

				<h1 class="title">Things I learned</h1>

				<ul>
					<li class="game-description game-list" v-for="(task, index) in projectData.learn" :key="index">
						{{ task }}
					</li>
				</ul>

			</div>

		</section>

		<section class="content-section reverse-layout">

			<h1 class="title">Scene System</h1>

			<video class="media-video right" controls aria-hidden="true">
				<source src="/gallusengine/src/mp4/scene_c.mp4" type="video/mp4" />
				<source src="/gallusengine/src/mp4/scene.webm" type="video/webm" />
				Your browser does not support the video tag.
			</video>

			<p class="game-description">The <Accent>Scene Window</Accent> is the primary interface for interacting with and manipulating entities within the active scene. It presents a real-time view of the world and provides tools for editing entity transforms, adjusting scene parameters, and managing scene content.</p>
			
			<p class="game-description">The editor exposes the current transformation operation, allowing users to switch between:</p>

			<ul>
				<li class="game-description game-list">Position (<Accent>Translation</Accent>).</li>
				<li class="game-description game-list"><Accent>Rotation</Accent>.</li>
				<li class="game-description game-list"><Accent>Scale</Accent>.</li>
			</ul>

			<p class="game-description">The <Accent>Hierarchy Window</Accent> allows the user to look for specific entities and select them. The <Accent>Inspector Window</Accent> allows the user to <Accent>add</Accent>, <Accent>remove</Accent> and <Accent>change components</Accent> on the selected entity. Entities can also be <Accent>renamed</Accent> and <Accent>deleted</Accent>.</p>
			
			<p class="game-description">Everything in the scene can be undone and redone with the editor action system, with buttons at the top for <Accent>Undo</Accent> and <Accent>Redo</Accent>.</p>
			
			<p class="game-description">You can switch between <Accent>editor camera</Accent> and <Accent>game camera</Accent> and <Accent>change the lighting</Accent>.</p>

		</section>

		<section class="content-section reverse-layout">

			<h1 class="title">Sprite Sheet Editor</h1>

			<video class="media-video left" controls aria-hidden="true">
				<source src="/gallusengine/src/mp4/sprite_sheet_editor_c.mp4" type="video/mp4" />
				<source src="/gallusengine/src/mp4/sprite_sheet_editor.webm" type="video/webm" />
				Your browser does not support the video tag.
			</video>

			<p class="game-description">The <Accent>Sprite Sheet Editor</Accent> is the primary interface for editing sprite sheets and rects. You can <Accent>add</Accent>, <Accent>remove</Accent> and <Accent>change rects</Accent>. Existing rects can be viewed on the side.</p>

		</section>

		<section class="content-section reverse-layout">

			<h1 class="title">Explorer Window</h1>

			<video class="media-video right" controls aria-hidden="true">
				<source src="/gallusengine/src/mp4/explorer_window_c.mp4" type="video/mp4" />
				<source src="/gallusengine/src/mp4/explorer_window.webm" type="video/webm" />
				Your browser does not support the video tag.
			</video>

			<p class="game-description">The <Accent>Explorer Window</Accent> in the engine editor functions as the primary interface for browsing, organizing, and interacting with project assets stored in the <Accent>asset directory</Accent>. It is conceptually similar to a file explorer, but with engine-specific asset types and editor integration.</p>
			
			<p class="game-description">The Explorer Window supports two primary viewing modes:</p>

			<ul>

				<li class="game-description game-list">Displays assets in a vertical list.</li>
				<li class="game-description game-list">Displays assets as a grid of thumbnails or icons.</li>

			</ul>

		</section>

		<section class="content-section reverse-layout">

			<h1 class="title">Console Window</h1>

			<video class="media-video left" controls aria-hidden="true">
				<source src="/gallusengine/src/mp4/console_window_c.mp4" type="video/mp4" />
				<source src="/gallusengine/src/mp4/console_window.webm" type="video/webm" />
				Your browser does not support the video tag.
			</video>

			<p class="game-description">The <Accent>Console Window</Accent> serves as the editor’s central interface for displaying and managing logged messages generated by the engine and editor systems. It provides developers with immediate feedback about runtime events, diagnostics, warnings, and errors occurring during development.</p>

		</section>

		<section class="content-section reverse-layout">

			<h1 class="title">Automating Custom Icon Fonts</h1>

			<video class="media-video right" controls aria-hidden="true">
				<source src="/gallusengine/src/mp4/font_generation_tool_c.mp4" type="video/mp4" />
				<source src="/gallusengine/src/mp4/font_generation_tool.webm" type="video/webm" />
				Your browser does not support the video tag.
			</video>

			<p class="game-description">As my game engine project grew, so did my need for icons. Font Awesome wasn"t enough because icons were missing, and I constantly found myself visiting third-party websites to generate custom icon fonts. Frustrated by the process, I decided to <Accent>build my own solution</Accent>.</p>
			
			<p class="game-description">I developed a <Accent>Node.js/NPM tool</Accent> that <Accent>automates icon font generation</Accent>. It works by:</p>

			<ul>
				<li class="game-description game-list">Taking icons from a folder.</li>
				<li class="game-description game-list">Creating an SVG font.</li>
				<li class="game-description game-list">Generating a font file from the SVG.</li>
				<li class="game-description game-list">Producing an HTML file for easy previewing and encoding reference.</li>
			</ul>

			<p class="game-description">To integrate seamlessly with my engine, the tool also generates a <Accent>C++ header file</Accent>, defining all <Accent>icon codes and embedding the font as a byte array</Accent>, allowing ImGui to load it directly from memory.</p>

			<p class="game-description">This tool eliminated my dependency on external services and made it effortless to expand my engine"s icon set.</p>

		</section>

	</div>

</template>

<script lang="ts">
import { defineComponent } from "vue";
import Accent from "../Accent/Accent.vue";

export default defineComponent({
	name: "GallusEngine",

	components: {
		Accent
	},

	props: {
		projectData: {
			type: Object,
			required: true
		}
	},

	setup() {
		return {};
	}
});
</script>

<style lang="scss" scoped>

@import url("/public/src/css/project.scss");

</style>